import {captureException} from '@sentry/browser';
import {Extras, ScopeContext} from '@sentry/types';

import * as logging from 'util/Logging';

interface ErrorOptions {
  /** Override the Error to throw. Defaults to Error. */
  ErrorClass: new (message?: string) => Error;
  /** Override the prefix for error messages. Defaults to "Assertion Error: " */
  errorPrefix?: string;
}

const DEFAULT_ERROR_OPTIONS: ErrorOptions = {
  ErrorClass: Error,
  errorPrefix: 'Assertion Error: ',
};

function mergeScopeExtras(extra?: Extras, scope?: Partial<ScopeContext>): Partial<ScopeContext> {
  if (scope == null) {
    scope = {};
  }

  if (extra != null) {
    scope.extra = {...scope.extra, ...extra};
  }
  return scope;
}

// if condition is false, throws a new error
function assert(
  condition: boolean,
  message: string,
  extra?: Extras,
  scope?: Partial<ScopeContext>,
  errorOptions: Partial<ErrorOptions> = {}
): asserts condition {
  if (condition) {
    return;
  }
  const {ErrorClass, errorPrefix} = getDefaultedErrorOptions(errorOptions);
  const errorMessage = getErrorMessage(message, errorPrefix);
  const exception = new ErrorClass(errorMessage);
  captureException(exception, mergeScopeExtras(extra, scope));
  throw exception;
}

// if condition is false, logs a warning (to Slack in production) but does not throw an error
function assertAndContinue(
  condition: boolean,
  message: string,
  extra?: Extras,
  scope?: Partial<ScopeContext>,
  errorOptions: Partial<ErrorOptions> = {}
): boolean {
  if (!condition) {
    const {ErrorClass, errorPrefix} = getDefaultedErrorOptions(errorOptions);
    const errorMessage = getErrorMessage(message, errorPrefix);
    logging.warn(errorMessage, mergeScopeExtras(extra, scope));
    if (window.debug || global.__NODE_TESTING__) {
      throw new ErrorClass(errorMessage);
    }
  }
  return condition;
}

function assertEqual<T>(
  actual: T,
  expected: T,
  message: string,
  extra?: Extras,
  scope?: Partial<ScopeContext>,
  errorOptions: Partial<ErrorOptions> = {}
): void {
  if (extra == null) {
    extra = {};
  }
  extra = {...extra, actual, expected};
  assert(actual === expected, message, extra, scope, errorOptions);
}

// Useful for type narrowing and chaining values that shouldn't be null
function assertNotNullAndReturn<T>(value: T): NonNullable<T> {
  assert(value != null, 'Value must not be null or undefined');
  return value;
}

/**
 * Only execute condition function if in debug mode
 * Useful for asserting expensive conditions outside of production
 */
function debugAssert(
  conditionCb: () => boolean,
  message: string,
  extra?: Extras,
  scope?: Partial<ScopeContext>,
  errorOptions: Partial<ErrorOptions> = {}
): void {
  if (window.debug || global.__NODE_TESTING__) {
    assert(conditionCb(), message, extra, scope, errorOptions);
  }
}

export {assert, assertAndContinue, assertEqual, assertNotNullAndReturn, debugAssert};

function getDefaultedErrorOptions(errorOptionOverrides: Partial<ErrorOptions>): ErrorOptions {
  return {...DEFAULT_ERROR_OPTIONS, ...errorOptionOverrides} as ErrorOptions;
}

function getErrorMessage(message: string, prefix?: string): string {
  return `${prefix ?? ''}${message}`;
}
